import { useState } from "react";
import { Mail, Phone, MapPin, Instagram, Linkedin, Facebook, Twitter } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not send.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="section-pad container-luxe">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline">Reach Out</p>
            <h1 className="font-display text-5xl md:text-6xl text-aubergine mt-6 leading-tight">
              Let's talk. <em className="text-rose not-italic">Always.</em>
            </h1>
            <p className="mt-6 text-plumSoft font-light text-lg leading-relaxed">
              For chapter partnerships, press, sponsorships, speaking enquiries or member support — we read every message.
            </p>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-lavender text-plum flex items-center justify-center"><Mail className="w-4 h-4" /></div>
                <div>
                  <p className="overline">Email</p>
                  <p className="text-aubergine">wowshe2026@gmail.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-lavender text-plum flex items-center justify-center"><Phone className="w-4 h-4" /></div>
                <div>
                  <p className="overline">Phone</p>
                  <a href="tel:+917095414575" className="text-aubergine hover:text-plum">+91 70954 14575</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-lavender text-plum flex items-center justify-center"><MapPin className="w-4 h-4" /></div>
                <div>
                  <p className="overline">Launching In</p>
                  <p className="text-aubergine">Hyderabad, Telangana · India</p>
                  <p className="text-xs text-plumSoft mt-1">Bengaluru · Chennai · Mumbai · Delhi NCR — coming through 2026</p>
                </div>
              </div>
            </div>

            <div className="mt-10 flex gap-3">
              {[Instagram, Linkedin, Facebook, Twitter].map((Icon, i) => (
                <a key={i} href="#" className="w-11 h-11 rounded-full border border-aubergine/20 flex items-center justify-center text-aubergine hover:bg-aubergine hover:text-ivory transition-all">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>

            <div className="mt-10 rounded-2xl overflow-hidden border border-aubergine/10">
              <iframe
                title="WOWSHE Hyderabad Map"
                src="https://www.google.com/maps?q=Hyderabad+Telangana+India&output=embed"
                className="w-full h-64"
                loading="lazy"
              />
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-6 lg:col-start-7 card-luxe p-8 md:p-10">
            <h2 className="font-display text-3xl text-aubergine">Send us a note</h2>
            <p className="text-sm text-plumSoft mt-2 font-light">We reply within one working day.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
              <div>
                <label className="label-luxe">Name</label>
                <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-luxe" />
              </div>
              <div>
                <label className="label-luxe">Phone</label>
                <input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-luxe" />
              </div>
              <div className="md:col-span-2">
                <label className="label-luxe">Email</label>
                <input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-luxe" />
              </div>
              <div className="md:col-span-2">
                <label className="label-luxe">Subject</label>
                <input data-testid="contact-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="input-luxe" />
              </div>
              <div className="md:col-span-2">
                <label className="label-luxe">Message</label>
                <textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-luxe" />
              </div>
            </div>
            <button data-testid="contact-submit" disabled={busy} className="btn-primary mt-8">
              {busy ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
